import React, { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { UNITS, VAT_RATES } from "@/lib/salesConstants";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = { name: "", description: "", unit: "stk", unit_price: "", vat_rate: 25, sku: "", active: true };

export const ProductSheet = ({ open, onOpenChange, product, onSaved, defaults }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const editing = !!product?.id;

  useEffect(() => {
    if (open) {
      setForm(product ? { ...EMPTY, ...product } : { ...EMPTY, unit: defaults?.default_unit || "stk", vat_rate: defaults?.default_vat_rate ?? 25 });
      setError("");
    }
  }, [open, product, defaults]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Navn er påkrevd"); return; }
    if (form.unit_price === "" || Number.isNaN(Number(form.unit_price))) { setError("Oppgi en gyldig pris"); return; }
    setSaving(true);
    setError("");
    const payload = { name: form.name, description: form.description || null, unit: form.unit || "stk", unit_price: Number(form.unit_price), vat_rate: Number(form.vat_rate), sku: form.sku || null, active: form.active !== false };
    try {
      const res = editing ? await api.put(`/sales/products/${product.id}`, payload) : await api.post("/sales/products", payload);
      toast.success(editing ? "Produkt oppdatert" : "Produkt opprettet");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg bg-card overflow-y-auto" data-testid="product-sheet">
        <SheetHeader>
          <SheetTitle>{editing ? "Rediger produkt" : "Nytt produkt eller tjeneste"}</SheetTitle>
          <SheetDescription>Produkter gjør det raskt å legge til linjer på fakturaen.</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="mt-6 space-y-5" noValidate>
          <div className="space-y-1.5"><Label htmlFor="p-name">Navn *</Label><Input id="p-name" value={form.name} onChange={set("name")} required data-testid="product-name-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="p-desc">Beskrivelse (vises på faktura)</Label><Textarea id="p-desc" rows={2} value={form.description || ""} onChange={set("description")} data-testid="product-description-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label htmlFor="p-price">Pris eks. mva *</Label><Input id="p-price" type="number" step="0.01" min="0" inputMode="decimal" value={form.unit_price} onChange={set("unit_price")} data-testid="product-price-input" /></div>
            <div className="space-y-1.5">
              <Label>MVA-sats</Label>
              <Select value={String(form.vat_rate)} onValueChange={(v) => setForm((f) => ({ ...f, vat_rate: Number(v) }))}>
                <SelectTrigger data-testid="product-vat-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">{VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r} %</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Enhet</Label>
              <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
                <SelectTrigger data-testid="product-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label htmlFor="p-sku">Varenr (valgfritt)</Label><Input id="p-sku" value={form.sku || ""} onChange={set("sku")} data-testid="product-sku-input" /></div>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><p className="text-sm font-medium">Aktiv</p><p className="text-xs text-muted-foreground">Inaktive produkter vises ikke i fakturaeditoren.</p></div>
            <Switch checked={form.active !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="product-active-switch" />
          </div>
          {error ? <p className="text-sm text-destructive" role="alert" data-testid="product-form-error">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="product-cancel-button">Avbryt</Button>
            <Button type="submit" disabled={saving} data-testid="product-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editing ? "Lagre" : "Opprett"}</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};
